import clsx from "clsx";
import { sortBy } from "lodash";
import React, { useEffect } from "react";
import { EquipmentCatalogItem, Experience, TimeslotUnit, UnitType } from "types";
import shallow from "zustand/shallow";
import { EquipmentLineItem, useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { NoTranslate } from "../Common/NoTranslate";
import { QuantitySpinner } from "../Common/QuantitySpinner/QuantitySpinner";
import { getCatalogItemsByUnitType } from "@/models/Catalog";

export const CreatePurchaseEquipmentPicker = React.memo(() => {
    const experience = useCreatePurchaseStore((state) => state.getActiveProduct<Experience>());
    const timeslotUnits = useCreatePurchaseStore((state) => state.getActiveItem().timeslot?.units);
    const equipmentLineItems = useCreatePurchaseStore((state) => state.getEquipmentLineItems(), shallow);
    const totalEquipmentQuantity = equipmentLineItems.reduce((sum, lineItem) => sum + lineItem.quantity, 0);

    const equipmentCatalogItems = getCatalogItemsByUnitType(experience.catalog?.items ?? [], UnitType.EQUIPMENT);
    const sortedItems = sortBy(equipmentCatalogItems as EquipmentCatalogItem[], "sequence");
    const activeItemIndex = useCreatePurchaseStore((state) => state.activeItemIndex);
    const timeslotId = useCreatePurchaseStore((state) => state.getActiveItem().timeslot?.id);

    // The seeded default is chosen before any timeslot exists, so it can land on equipment that is
    // unavailable once one is selected. Keyed on the timeslot alone: quantity edits never re-run this.
    useEffect(() => {
        const state = useCreatePurchaseStore.getState();
        const item = state.purchase.items[activeItemIndex];
        const lineItems = item?.equipmentLineItems;
        if (!lineItems || !item.timeslot) {
            return;
        }

        const isUnitAvailable = (templateId?: string) => {
            const unit = item.timeslot?.units?.find((u) => u.template?.id === templateId);
            return unit === undefined || ((unit as { count?: number }).count ?? 0) > 0;
        };

        const total = lineItems.reduce((sum, li) => sum + li.quantity, 0);
        const defaulted = lineItems.find((li) => li.quantity === 1);
        if (total !== 1 || !defaulted || isUnitAvailable(defaulted.template?.id)) {
            return;
        }

        const firstAvailableCatalogItem = sortedItems.find((catalogItem) => isUnitAvailable(catalogItem.template?.id));
        if (!firstAvailableCatalogItem || firstAvailableCatalogItem.template?.id === defaulted.template?.id) {
            return;
        }

        state.actions.updateEquipmentQuantity(
            activeItemIndex,
            firstAvailableCatalogItem.template?.id,
            1,
            firstAvailableCatalogItem.name,
            firstAvailableCatalogItem.sequence,
        );
        state.actions.updateEquipmentQuantity(
            activeItemIndex,
            defaulted.template.id,
            0,
            defaulted.name ?? "",
            defaulted.sequence,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeslotId, activeItemIndex]);

    if (sortedItems.length === 0) {
        return null;
    }

    const minError = totalEquipmentQuantity < 1 ? "Quantity must be at least 1" : undefined;

    return (
        <div className="space-y-2">
            <div className={clsx("text-sm font-bold", { "text-danger": minError })}>{minError ?? "Quantity"}</div>
            <div className="flex flex-col space-y-2">
                {sortedItems.map((catalogItem) => (
                    <EquipmentItem
                        key={catalogItem.template?.id ?? catalogItem.id ?? catalogItem.name}
                        catalogItem={catalogItem}
                        timeslotUnits={timeslotUnits}
                        totalEquipmentQuantity={totalEquipmentQuantity}
                    />
                ))}
            </div>
        </div>
    );
});

interface EquipmentItemProps {
    catalogItem: EquipmentCatalogItem;
    timeslotUnits?: TimeslotUnit[];
    totalEquipmentQuantity?: number;
}

export const EquipmentItem = ({ catalogItem, timeslotUnits, totalEquipmentQuantity }: EquipmentItemProps) => {
    const activeItemIndex = useCreatePurchaseStore((state) => state.activeItemIndex);

    const { quantity } = useCreatePurchaseStore((state) => {
        const match = state
            .getEquipmentLineItems()
            .find((lineItem: EquipmentLineItem) => lineItem.template?.id === catalogItem.template?.id);
        return { quantity: match?.quantity ?? 0 };
    }, shallow);

    const timeslotUnit = timeslotUnits?.find((u) => u.template?.id === catalogItem.template?.id);
    const count = timeslotUnit?.count;
    const max = timeslotUnit ? count : catalogItem.count;
    const isUnavailable = timeslotUnit !== undefined && count === 0;
    const error =
        !isUnavailable && count !== undefined && quantity > count ? `Quantity cannot exceed ${count}` : undefined;

    const spare = (totalEquipmentQuantity ?? 1) - 1;
    const min = totalEquipmentQuantity === undefined ? 0 : Math.max(quantity - spare, 0);

    const handleChange = (newQuantity: number) => {
        useCreatePurchaseStore
            .getState()
            .actions.updateEquipmentQuantity(
                activeItemIndex,
                catalogItem.template?.id,
                newQuantity,
                catalogItem.name,
                catalogItem.sequence,
            );
    };

    return (
        <div className="flex items-center justify-start" data-testid="create-purchase-equipment-picker-item">
            <QuantitySpinner
                shouldSyncValue={false}
                maxTooltipText={isUnavailable ? "" : "You have reached the maximum available quantity of"}
                value={quantity}
                min={min}
                max={max}
                size="large"
                classNames={{ fieldset: "mr-3", label: "font-bold" }}
                onChange={handleChange}
            />
            <div className="min-w-0 flex-1">
                <NoTranslate as="div" className={clsx("break-all font-bold", { "text-danger": isUnavailable })}>
                    {catalogItem.name}
                </NoTranslate>
                {catalogItem.caption && <div className="text-gray-darker line-clamp-2">{catalogItem.caption}</div>}
                {isUnavailable && <div className="text-sm text-danger">Unavailable</div>}
                {error && <div className="text-sm text-danger">{error}</div>}
            </div>
        </div>
    );
};
